"use client";
import { create } from "zustand";
import { fullPp, type PartyMember, type MoveLearnRequest } from "@/engine/party";
import type { EvolutionEvent } from "@/engine/evolution";
import { track, ANALYTICS_EVENTS } from "@/lib/analytics";

// Fire the `first_battle` funnel event only once per session. NO-OP without a
// PostHog key, so this is inert until analytics is configured.
let firstBattleTracked = false;

export type GameView = "overworld" | "department" | "section";
export type Direction = "up" | "down" | "left" | "right";

export interface Position {
  x: number;
  y: number;
}

export interface SurggieState {
  id: string;
  name: string;
  role: string;
  team: string;
  spriteKey: string;
  position: Position;
  status: "idle" | "working" | "resting";
  level: number;
  xp: number;
  llmModel?: string;
  isPrimary?: boolean;
}

export interface DepartmentState {
  id: string;
  name: string;
  type: string;
  gridX: number;
  gridY: number;
  level: number;
}

export interface SectionState {
  id: string;
  departmentId: string;
  name: string;
  type: string;
  gridX: number;
  gridY: number;
}

interface GameState {
  // View
  view: GameView;
  setView: (view: GameView) => void;

  // Player
  playerPosition: Position;
  playerDirection: Direction;
  playerAvatar: string;
  setPlayerPosition: (pos: Position) => void;
  setPlayerDirection: (dir: Direction) => void;
  setPlayerAvatar: (avatar: string) => void;

  // Current context
  currentDepartmentId: string | null;
  currentSectionId: string | null;
  setCurrentDepartment: (id: string | null) => void;
  setCurrentSection: (id: string | null) => void;

  // Departments & sections
  departments: DepartmentState[];
  sections: SectionState[];
  setDepartments: (depts: DepartmentState[]) => void;
  setSections: (sects: SectionState[]) => void;

  // Surggies
  surggies: SurggieState[];
  setSurggies: (s: SurggieState[]) => void;

  // Party (canonical HP-bearing battle party — see @/engine/party)
  party: PartyMember[];
  setParty: (p: PartyMember[]) => void;
  updatePartyMember: (index: number, patch: Partial<PartyMember>) => void;
  addPartyMember: (m: PartyMember) => boolean;
  healParty: () => void;

  // Evolution / move-learn queues (consumed by EvolutionScreen / MoveLearnPrompt)
  evolutionQueue: EvolutionEvent[];
  pushEvolution: (e: EvolutionEvent) => void;
  shiftEvolution: () => void;
  moveLearnQueue: MoveLearnRequest[];
  pushMoveLearn: (r: MoveLearnRequest) => void;
  shiftMoveLearn: () => void;

  // Interaction
  interactionTarget: { type: "surggie" | "section" | "building" | "gem"; id: string } | null;
  setInteractionTarget: (target: GameState["interactionTarget"]) => void;
  showInteractionPrompt: boolean;
  setShowInteractionPrompt: (show: boolean) => void;

  // Camera
  cameraOffset: Position;
  setCameraOffset: (offset: Position) => void;

  // Keys pressed
  keysPressed: Set<string>;
  pressKey: (key: string) => void;
  releaseKey: (key: string) => void;

  // Player XP/Level
  playerXp: number;
  playerLevel: number;
  addPlayerXp: (amount: number) => void;

  // Battle
  battle: BattleState;
  startBattle: (opts: {
    opponentSpeciesId: string;
    opponentLevel: number;
    opponentHpMax: number;
    isWild: boolean;
  }) => void;
  endBattle: (outcome: "victory" | "defeat" | "caught" | "fled") => void;
  setBattlePhase: (phase: BattleState["phase"]) => void;
  setOpponentHp: (hp: number) => void;
  setPlayerHp: (current: number, max?: number) => void;
  setPlayerStatus: (status: StatusEffect) => void;
  setOpponentStatus: (status: StatusEffect) => void;
  setBackdrop: (key: string) => void;
  appendBattleLog: (line: string) => void;
  setPendingMove: (moveId: string | null) => void;
  switchActivePartyMember: (index: number) => void;

  // Dialog
  dialogOpen: boolean;
  dialogPages: Array<{ text: string; speaker?: string; spriteKey?: string; choices?: Array<{ id: string; label: string }> }>;
  dialogNpcId: string | null;
  openDialog: (pages: GameState["dialogPages"], npcId?: string) => void;
  closeDialog: () => void;

  // Tutorial
  tutorialActive: boolean;
  tutorialStep: number;
  tutorialDismissed: boolean;
  startTutorial: () => void;
  advanceTutorial: () => void;
  dismissTutorial: () => void;

  // Current Map
  currentMapId: string;
  setCurrentMapId: (id: string) => void;
}

export type BattlePhase =
  | "idle"
  | "player_turn"
  | "move_selection"
  | "resolving"
  | "animating"
  | "opponent_turn"
  | "victory"
  | "defeat"
  | "caught"
  | "fled";

export type StatusKind = "none" | "poison" | "burn" | "paralyze" | "sleep";

export interface StatusEffect {
  kind: StatusKind;
  turns: number;
}

const NO_STATUS: StatusEffect = { kind: "none", turns: 0 };

export interface BattleState {
  active: boolean;
  isWild: boolean;
  opponentSpeciesId: string | null;
  opponentLevel: number;
  opponentHpCurrent: number;
  opponentHpMax: number;
  playerHpCurrent: number;
  playerHpMax: number;
  playerStatus: StatusEffect;
  opponentStatus: StatusEffect;
  backdropKey: string;
  activePartyIndex: number;
  log: string[];
  phase: BattlePhase;
  pendingMoveId: string | null;
}

const INITIAL_BATTLE_STATE: BattleState = {
  active: false,
  isWild: false,
  opponentSpeciesId: null,
  opponentLevel: 0,
  opponentHpCurrent: 0,
  opponentHpMax: 0,
  playerHpCurrent: 0,
  playerHpMax: 0,
  playerStatus: NO_STATUS,
  opponentStatus: NO_STATUS,
  backdropKey: "grass",
  activePartyIndex: 0,
  log: [],
  phase: "idle",
  pendingMoveId: null,
};

const XP_BASE = 100;
const XP_MULTIPLIER = 1.5;

export function xpToNextLevel(level: number): number {
  return Math.floor(XP_BASE * Math.pow(level, XP_MULTIPLIER));
}

export const useGameStore = create<GameState>((set, get) => ({
  view: "overworld",
  setView: (view) => set({ view }),

  playerPosition: { x: 400, y: 300 },
  playerDirection: "down",
  playerAvatar: "ceo_1",
  setPlayerPosition: (pos) => set({ playerPosition: pos }),
  setPlayerDirection: (dir) => set({ playerDirection: dir }),
  setPlayerAvatar: (avatar) => set({ playerAvatar: avatar }),

  currentDepartmentId: null,
  currentSectionId: null,
  setCurrentDepartment: (id) => set({ currentDepartmentId: id }),
  setCurrentSection: (id) => set({ currentSectionId: id }),

  departments: [],
  sections: [],
  setDepartments: (departments) => set({ departments }),
  setSections: (sections) => set({ sections }),

  surggies: [],
  setSurggies: (surggies) => set({ surggies }),

  party: [],
  setParty: (party) => set({ party }),
  updatePartyMember: (index, patch) =>
    set((state) => ({
      party: state.party.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    })),
  addPartyMember: (m) => {
    if (get().party.length >= 6) return false;
    set((state) => ({ party: [...state.party, m] }));
    return true;
  },
  healParty: () =>
    set((state) => ({
      party: state.party.map((m) => ({
        ...m,
        hpCurrent: m.hpMax,
        pp: fullPp(m.moves),
        fainted: false,
      })),
    })),

  evolutionQueue: [],
  pushEvolution: (e) =>
    set((state) => ({ evolutionQueue: [...state.evolutionQueue, e] })),
  shiftEvolution: () =>
    set((state) => ({ evolutionQueue: state.evolutionQueue.slice(1) })),
  moveLearnQueue: [],
  pushMoveLearn: (r) =>
    set((state) => ({ moveLearnQueue: [...state.moveLearnQueue, r] })),
  shiftMoveLearn: () =>
    set((state) => ({ moveLearnQueue: state.moveLearnQueue.slice(1) })),

  interactionTarget: null,
  setInteractionTarget: (target) => set({ interactionTarget: target }),
  showInteractionPrompt: false,
  setShowInteractionPrompt: (show) => set({ showInteractionPrompt: show }),

  cameraOffset: { x: 0, y: 0 },
  setCameraOffset: (offset) => set({ cameraOffset: offset }),

  keysPressed: new Set(),
  pressKey: (key) =>
    set((state) => {
      const next = new Set(state.keysPressed);
      next.add(key);
      return { keysPressed: next };
    }),
  releaseKey: (key) =>
    set((state) => {
      const next = new Set(state.keysPressed);
      next.delete(key);
      return { keysPressed: next };
    }),

  playerXp: 0,
  playerLevel: 1,
  addPlayerXp: (amount) =>
    set((state) => {
      let newXp = state.playerXp + amount;
      let newLevel = state.playerLevel;
      while (newXp >= xpToNextLevel(newLevel)) {
        newXp -= xpToNextLevel(newLevel);
        newLevel++;
      }
      return { playerXp: newXp, playerLevel: newLevel };
    }),

  battle: INITIAL_BATTLE_STATE,
  startBattle: ({ opponentSpeciesId, opponentLevel, opponentHpMax, isWild }) => {
    if (!firstBattleTracked) {
      firstBattleTracked = true;
      track(ANALYTICS_EVENTS.FIRST_BATTLE);
    }
    set({
      battle: {
        active: true,
        isWild,
        opponentSpeciesId,
        opponentLevel,
        opponentHpCurrent: opponentHpMax,
        opponentHpMax,
        playerHpCurrent: 0,
        playerHpMax: 0,
        playerStatus: NO_STATUS,
        opponentStatus: NO_STATUS,
        backdropKey: "grass",
        activePartyIndex: 0,
        log: [],
        phase: "player_turn",
        pendingMoveId: null,
      },
    });
  },
  endBattle: (outcome) =>
    set((state) => ({
      battle: {
        ...state.battle,
        active: false,
        phase: outcome,
        pendingMoveId: null,
      },
    })),
  setBattlePhase: (phase) =>
    set((state) => ({ battle: { ...state.battle, phase } })),
  setOpponentHp: (hp) =>
    set((state) => ({
      battle: {
        ...state.battle,
        opponentHpCurrent: Math.max(0, Math.min(hp, state.battle.opponentHpMax)),
      },
    })),
  setPlayerHp: (current, max) =>
    set((state) => {
      const nextMax = max ?? state.battle.playerHpMax;
      return {
        battle: {
          ...state.battle,
          playerHpMax: nextMax,
          playerHpCurrent: Math.max(0, Math.min(current, nextMax)),
        },
      };
    }),
  setPlayerStatus: (status) =>
    set((state) => ({ battle: { ...state.battle, playerStatus: status } })),
  setOpponentStatus: (status) =>
    set((state) => ({ battle: { ...state.battle, opponentStatus: status } })),
  setBackdrop: (key) =>
    set((state) => ({ battle: { ...state.battle, backdropKey: key } })),
  appendBattleLog: (line) =>
    set((state) => ({
      battle: {
        ...state.battle,
        log: [...state.battle.log, line].slice(-50),
      },
    })),
  setPendingMove: (moveId) =>
    set((state) => ({ battle: { ...state.battle, pendingMoveId: moveId } })),
  switchActivePartyMember: (index) =>
    set((state) => ({ battle: { ...state.battle, activePartyIndex: index } })),

  // Dialog
  dialogOpen: false,
  dialogPages: [],
  dialogNpcId: null,
  openDialog: (pages, npcId) => set({ dialogOpen: true, dialogPages: pages, dialogNpcId: npcId ?? null }),
  closeDialog: () => set({ dialogOpen: false, dialogPages: [], dialogNpcId: null }),

  // Tutorial
  tutorialActive: false,
  tutorialStep: 0,
  tutorialDismissed: false,
  startTutorial: () => set({ tutorialActive: true, tutorialStep: 0, tutorialDismissed: false }),
  advanceTutorial: () =>
    set((s) => ({
      tutorialStep: s.tutorialStep + 1,
      tutorialActive: s.tutorialStep + 1 <= 4,
    })),
  dismissTutorial: () => set({ tutorialActive: false, tutorialDismissed: true }),

  // Current Map
  currentMapId: "overworld",
  setCurrentMapId: (id) => set({ currentMapId: id }),
}));
